import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, Lock } from "lucide-react";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";

export default function GateEditor() {
  const { gateId } = useParams();
  const nav = useNavigate();
  const isNew = !gateId;
  const { pipelineId } = usePipelineCtx();
  const [gate, setGate] = useState({
    gate_name: "",
    description: "",
    gate_type: "weighted_scoring",
    pass_threshold: 70,
    criteria: [],
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) {
      api.get(`/gates/${gateId}`).then(({ data }) => {
        setGate(data);
        setLoading(false);
      });
    }
  }, [gateId, isNew]);

  const isPublished = gate.is_published;
  const isWeighted = gate.gate_type === "weighted_scoring";
  const totalWeight = (gate.criteria || []).reduce((a, c) => a + (Number(c.weight) || 0), 0);
  const weightError = isWeighted && gate.criteria?.length > 0 && Math.round(totalWeight) !== 100;
  const blankCriteria = (gate.criteria || [])
    .map((c, i) => ({ i, blank: !c.criteria_text || !c.criteria_text.trim() }))
    .filter((x) => x.blank);
  const hasBlankCriteria = blankCriteria.length > 0;

  const setField = (k, v) => setGate((g) => ({ ...g, [k]: v }));
  const setCriterion = (idx, k, v) =>
    setGate((g) => ({
      ...g,
      criteria: g.criteria.map((c, i) => (i === idx ? { ...c, [k]: v } : c)),
    }));
  const addCriterion = () =>
    setGate((g) => ({
      ...g,
      criteria: [
        ...(g.criteria || []),
        {
          criteria_text: "",
          // v5.8 A6: force criterion type to match gate type; Rubric retired
          criteria_type: g.gate_type === "weighted_scoring" ? "scored" : "pass_fail",
          weight: 0,
          max_score: 10,
          rubric_options: null,
          sort_order: (g.criteria || []).length,
        },
      ],
    }));
  const removeCriterion = (idx) =>
    setGate((g) => ({ ...g, criteria: g.criteria.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!gate.gate_name.trim()) {
      toast.error("Gate name is required");
      return;
    }
    if (hasBlankCriteria) {
      toast.error("Criterion name is required for all criteria");
      return;
    }
    if (weightError) {
      toast.error(`Criteria weights must total 100. Current total: ${totalWeight}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        gate_name: gate.gate_name,
        description: gate.description,
        gate_type: gate.gate_type,
        pass_threshold: gate.pass_threshold,
        criteria: (gate.criteria || []).map((c, i) => ({
          criteria_text: c.criteria_text,
          // v5.8 A6: type derived from gate type on save
          criteria_type: gate.gate_type === "weighted_scoring" ? "scored" : "pass_fail",
          weight: gate.gate_type === "weighted_scoring" ? (Number(c.weight) || null) : null,
          max_score: gate.gate_type === "weighted_scoring" ? Number(c.max_score) || 10 : null,
          rubric_options: null,
          sort_order: i,
        })),
      };
      if (isNew) {
        const { data } = await api.post("/gates", { ...payload, pipeline_id: pipelineId });
        toast.success("Gate created");
        nav(`/gates/${data.gate_id}`);
      } else if (isPublished) {
        // v5.6 #3: published gates allow only name & description edits
        await api.patch(`/gates/${gateId}/name`, {
          name: gate.gate_name,
          description: gate.description,
        });
        toast.success("Gate updated");
      } else {
        await api.patch(`/gates/${gateId}`, payload);
        toast.success("Gate updated");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!window.confirm("Publishing locks this gate permanently. Confirm publish?")) return;
    try {
      await api.post(`/gates/${gateId}/publish`);
      toast.success("Gate published");
      const { data } = await api.get(`/gates/${gateId}`);
      setGate(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <div className="p-10">Loading…</div>;

  return (
    <div className="p-8 max-w-3xl">
      <button onClick={() => nav("/gates")}
        className="text-sm inline-flex items-center gap-1 text-[var(--cw-muted)] hover:text-[var(--cw-primary)]"
        data-testid="back-to-gates">
        <ArrowLeft size={14} /> Back to Gates
      </button>

      <div className="mt-4 flex justify-between items-start">
        <div>
          <div className="cw-caption uppercase tracking-widest">
            {isNew ? "New" : isPublished ? "Published" : "Draft"} Gate
          </div>
          <h1 className="cw-h1 mt-1">
            {isNew ? "Create Gate Framework" : gate.gate_name}
          </h1>
        </div>
        {!isNew && !isPublished && (
          <button onClick={publish} className="cw-btn-primary inline-flex items-center gap-1" data-testid="publish-btn">
            <Lock size={14} /> Publish
          </button>
        )}
      </div>

      {isPublished && (
        <div className="mt-4 p-3 rounded-md text-sm"
          style={{ background: "rgba(40,42,116,0.06)", color: "var(--cw-primary)" }}
          data-testid="published-banner">
          This gate is published. Criteria and scoring are locked, but name and description can still be edited.
        </div>
      )}

      <div className="mt-6 space-y-4">
        <div className="cw-card p-5 space-y-4">
          <Field label="Gate name *">
            <input value={gate.gate_name}
              onChange={(e) => setField("gate_name", e.target.value)}
              className="w-full h-10 px-3 rounded-md border text-sm disabled:opacity-70"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid="gate-name-input" />
          </Field>
          <Field label="Description">
            <textarea value={gate.description || ""} rows={2}
              onChange={(e) => setField("description", e.target.value)}
              className="w-full px-3 py-2 rounded-md border text-sm disabled:opacity-70"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid="gate-desc-input" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Gate type">
              {/* v5.8 Addition 6: gate type locked once first criterion is added */}
              <select value={gate.gate_type}
                disabled={isPublished || (gate.criteria || []).length > 0}
                title={(gate.criteria || []).length > 0
                  ? "Gate type cannot be changed once criteria exist. Duplicate this gate to start over with a different type."
                  : undefined}
                onChange={(e) => setField("gate_type", e.target.value)}
                className="w-full h-10 px-3 rounded-md border text-sm bg-white disabled:opacity-70"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid="gate-type-select">
                <option value="weighted_scoring">Weighted scoring</option>
                <option value="hard_filter">Hard filter (pass/fail only)</option>
              </select>
            </Field>
            {isWeighted && (
              <Field label="Pass threshold (%)">
                <input type="number" min={0} max={100} value={gate.pass_threshold || 0}
                  disabled={isPublished}
                  onChange={(e) => setField("pass_threshold", Number(e.target.value))}
                  className="w-full h-10 px-3 rounded-md border text-sm disabled:opacity-70"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid="gate-threshold-input" />
              </Field>
            )}
          </div>
          <div className="cw-caption">Scoring mode: User Only (Phase 1)</div>
        </div>

        <div className="cw-card p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="cw-h3">Criteria</h3>
            {!isPublished && (
              <button onClick={addCriterion} className="cw-btn-ghost text-sm inline-flex items-center gap-1" data-testid="add-criterion-btn">
                <Plus size={14} /> Add Criterion
              </button>
            )}
          </div>
          <div className="space-y-3">
            {(gate.criteria || []).map((c, i) => (
              <div key={i} className="border rounded-md p-3" style={{ borderColor: "var(--cw-border)" }}
                data-testid={`criterion-${i}`}>
                <div className="flex justify-between items-start gap-2 mb-2">
                  <div className="flex-1">
                    <input value={c.criteria_text} disabled={isPublished}
                      onChange={(e) => setCriterion(i, "criteria_text", e.target.value)}
                      placeholder="e.g. Traction meets Series A bar"
                      className="w-full h-9 px-3 rounded-md border text-sm disabled:opacity-70"
                      style={{
                        borderColor: (!c.criteria_text || !c.criteria_text.trim())
                          ? "var(--cw-error)"
                          : "var(--cw-border)",
                      }}
                      data-testid={`criterion-text-${i}`} />
                    {!isPublished && (!c.criteria_text || !c.criteria_text.trim()) && (
                      <div className="text-xs mt-1" style={{ color: "var(--cw-error)" }}
                        data-testid={`criterion-text-error-${i}`}>
                        Criterion name is required
                      </div>
                    )}
                  </div>
                  {!isPublished && (
                    <button onClick={() => removeCriterion(i)} className="text-[var(--cw-error)] p-1" data-testid={`remove-criterion-${i}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <label className="cw-caption">Type</label>
                    {/* v5.8 Addition 6: type locked to gate type; Rubric retired */}
                    <select value={isWeighted ? "scored" : "pass_fail"} disabled>
                      <option value="scored">Scored</option>
                      <option value="pass_fail">Pass/Fail</option>
                    </select>
                  </div>
                  {isWeighted && (
                    <div>
                      <label className="cw-caption">Weight (%)</label>
                      <input type="number" min={0} max={100} disabled={isPublished}
                        value={c.weight ?? ""} onChange={(e) => setCriterion(i, "weight", e.target.value)}
                        className="w-full h-8 px-2 rounded-md border text-xs disabled:opacity-70"
                        style={{ borderColor: "var(--cw-border)" }} />
                    </div>
                  )}
                  {c.criteria_type === "scored" && (
                    <div>
                      <label className="cw-caption">Max score</label>
                      <input type="number" min={1} disabled={isPublished}
                        value={c.max_score ?? 10} onChange={(e) => setCriterion(i, "max_score", e.target.value)}
                        className="w-full h-8 px-2 rounded-md border text-xs disabled:opacity-70"
                        style={{ borderColor: "var(--cw-border)" }} />
                    </div>
                  )}
                </div>
                {c.criteria_type === "rubric" && (
                  <div className="mt-2">
                    <label className="cw-caption">Options (comma-separated)</label>
                    <input disabled={isPublished}
                      value={Array.isArray(c.rubric_options) ? c.rubric_options.join(", ") : (c.rubric_options || "")}
                      onChange={(e) => setCriterion(i, "rubric_options", e.target.value)}
                      placeholder="e.g. Excellent, Good, Average, Weak"
                      className="w-full h-8 px-2 rounded-md border text-xs disabled:opacity-70"
                      style={{ borderColor: "var(--cw-border)" }} />
                  </div>
                )}
              </div>
            ))}
            {(gate.criteria || []).length === 0 && (
              <div className="text-xs text-[var(--cw-muted)] italic">No criteria yet.</div>
            )}
          </div>

          {isWeighted && (gate.criteria || []).length > 0 && (
            <div className="mt-3 text-xs" style={{ color: weightError ? "var(--cw-error)" : "var(--cw-muted)" }}
              data-testid="weight-total">
              Total weight: {totalWeight}% {weightError && `— must equal 100`}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving || (!isPublished && hasBlankCriteria)}
            className="cw-btn-primary inline-flex items-center gap-2"
            data-testid="save-gate-btn">
            <Save size={14} /> {saving ? "Saving…" : (isNew ? "Create Gate" : (isPublished ? "Save Name / Description" : "Save Changes"))}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="cw-caption block mb-1">{label}</label>
      {children}
    </div>
  );
}
